/**
 * Pure-data English catalogue for namespace 'expr' (W1-10c core-expr; imports only core/util/types).
 * Keys are written WITHOUT the namespace prefix; core produces MessageRefs such as 'expr.unknownName'.
 *
 * Groups: parse errors (lexer, parser, checker), evaluation errors, and the help reference (variables,
 * constants, functions, units, assignment operators) returned by `exprReference`.
 * Messages avoid apostrophes (ICU quoting) and use double quotes around user text.
 */

import type { MessageCatalog } from '../util/types';

const catalog: MessageCatalog = {
  ns: 'expr',
  locale: 'en',
  messages: {
    // Lexer and parser
    empty: 'Type a number or an expression, for example 12, +=10 or lerp(-400, 400, u)',
    emptyAfterAssign: 'Type a value after "{op}", for example {op} 10',
    unexpectedChar: 'The character "{char}" cannot be used in an expression',
    unexpectedToken: 'Unexpected "{token}"',
    unexpectedEnd: 'Something is missing at the end',
    expectedValue: 'Expected a number, a name or "(" instead of "{token}"',
    missingOperator: 'Missing an operator such as * or + before "{token}"',
    unitSpace: 'Write the unit right after the number, as in {example}',
    unitSpaceOrMul: 'Write {example} for the unit, or {num} * {name} to multiply',
    percentOrRemainder:
      'Add spaces to say what you mean: {num}% {sign} {rest} is a percentage, {num} % {sign}{rest} is the remainder',
    percentSpace:
      'Missing an operator before "{rest}": write {num}% * {rest} to multiply the percentage, or {num} % {rest} for the remainder',
    unclosedParen: 'This "(" is never closed: add ")"',
    unmatchedParen: 'This ")" has no matching "("',
    strayComma: 'Commas only separate the values of a function, as in max(a, b)',
    assignNotFirst: '"{op}" must come first, as in {op} 10',
    assignHere: 'Type only the value here, without "{op}"',
    unclosedString: 'Close the curve name with a matching quote',
    badDollar: 'Write $t$ or simply t',
    badNumber: '"{text}" is not a valid number',
    numberTooLarge: 'This number is too large',
    tooDeep: 'This expression is nested too deeply',

    // Checker: names, functions and units
    unknownUnit: 'Unknown unit "{unit}". Units: px, %, deg, s, ms, b (beats) and v (speed)',
    implicitMul: 'Write {num} * {name} to multiply',
    speedUnitHere: 'The speed unit v only works on motion values such as keyframe and segment values',
    unknownName: 'Unknown name "{name}"',
    unknownNameSuggest: 'Unknown name "{name}". Did you mean {suggestion}?',
    varNotHere: '"{name}" cannot be used here. You can use: {list}',
    notAFunction: '"{name}" is a value, not a function',
    unknownFunction: 'Unknown function "{name}"',
    unknownFunctionSuggest: 'Unknown function "{name}". Did you mean {suggestion}?',
    needsCall: '{name} is a function: write {signature}',
    argCount: '{signature} takes {count, plural, one {# value} other {# values}}',
    argCountRange: '{signature} takes {min} to {max} values',
    argCountMin: '{signature} needs at least {min, plural, one {# value} other {# values}}',
    stringHere: 'Text in quotes is only used for curve names, as in ease("outQuad", u)',
    unknownEasing: 'Unknown curve "{name}". Use a curve number from 1 to 29 or a name such as outQuad',
    unknownEasingSuggest: 'Unknown curve "{name}". Did you mean {suggestion}?',
    easingId: 'Curve numbers are whole numbers from 1 to 29',
    mixedUnits: 'Values in {a} and {b} cannot be combined here',
    mixedTime: 'Beats and seconds cannot be mixed: convert with beat(...) or sec(...)',
    unitProduct: 'Two values with units cannot be multiplied ({a} and {b})',
    unitQuotient: 'Dividing by a value in {b} only works for a value in the same unit',
    speedUnitOp: 'Speed values (v) can only be added, subtracted or scaled by a number',
    scaleByUnit: '{op} cannot use the speed unit v: use =, += or -=',
    fieldWantsBeats: 'This field takes beats: convert seconds with beat(...)',
    fieldWantsSeconds: 'This field takes seconds: convert beats with sec(...)',
    timeUnitUnknown: 'Type the time without "{unit}", in the unit of this field',
    fieldNotTime: 'This field does not take a time: remove "{unit}"',
    timeAsNumber: 'A time cannot be used as a plain number here: divide it by a time, as in {example}',
    beatArg: 'beat(...) converts a time in seconds into a beat',
    secArg: 'sec(...) converts a beat into seconds',

    // Evaluation
    divByZero: 'Division by zero',
    modByZero: 'The remainder of a division by zero is undefined',
    undefinedFn: '{name}(...) is undefined for this value',
    undefinedPow: 'This power is undefined (for example a negative number to a fractional power)',
    tooLarge: 'The result is too large',
    notFinite: 'The result is not a valid number',
    missingValue: 'This item has no value for "{name}"',
    needsTempo: '{name}(...) needs the tempo map, which is not available here',
    needsSegment: 'The speed unit v needs a segment: this item has no segment start value or duration',
    roundDigits: 'round(x, digits) takes a whole number of digits from -15 to 15',
    easingRuntime: 'ease(...) needs a whole curve number from 1 to 29, not {id}',

    // Reference: variables and constants
    'var.v': 'The current value of this field',
    'var.i': 'Number of the item in time order, starting at 0',
    'var.n': 'Number of selected items',
    'var.u': 'Position in the selection, from 0 (first item) to 1 (last item)',
    'var.t': 'Beat of the item',
    'var.s': 'Time of the item in seconds',
    'var.x': 'X position of the note',
    'var.speed': 'Speed of the note',
    'var.size': 'Width of the note',
    'var.v0': 'Value at the start of the segment',
    'var.v1': 'Value at the end of the segment',
    'var.len': 'Length of the segment in beats',
    'var.pathT': 'Progress along the path, from 0 (start) to 1 (end)',
    'var.pathBeat': 'Beat at this point of the path',
    'const.pi': 'The number pi, 3.14159...',
    'const.e': 'The number e, 2.71828...',

    // Reference: functions
    'fn.abs': 'Distance from zero: abs(-3) is 3',
    'fn.min': 'The smallest of the values',
    'fn.max': 'The largest of the values',
    'fn.clamp': 'Keeps x between lo and hi',
    'fn.round': 'Rounds to the nearest whole number, or to a number of decimals',
    'fn.floor': 'Rounds down',
    'fn.ceil': 'Rounds up',
    'fn.sqrt': 'Square root',
    'fn.pow': 'x to the power y (same as x ^ y)',
    'fn.exp': 'e to the power x',
    'fn.log': 'Natural logarithm, or the logarithm in the given base',
    'fn.sin': 'Sine (radians, or degrees written as 30deg)',
    'fn.cos': 'Cosine (radians, or degrees written as 30deg)',
    'fn.tan': 'Tangent (radians, or degrees written as 30deg)',
    'fn.asin': 'Inverse sine, in radians',
    'fn.acos': 'Inverse cosine, in radians',
    'fn.atan': 'Inverse tangent, in radians',
    'fn.atan2': 'Angle of the point (x, y), in radians',
    'fn.sign': '1 for positive values, -1 for negative values, 0 for zero',
    'fn.lerp': 'Goes from a to b as u goes from 0 to 1',
    'fn.ease':
      'Applies a curve to u (0 to 1): an RPE curve number 1 to 29 or a name such as outSine, optionally only part of the curve',
    'fn.bezier': 'Applies a custom curve (cubic Bezier handles) to u',
    'fn.alt': 'Cycles through the values item by item: the first item gets a, the second b, and so on',
    'fn.rand': 'A random number between a and b, the same every time for the same seed',
    'fn.randn': 'A random number around mu with spread sigma (normal distribution)',
    'fn.snap': 'Rounds x to the nearest multiple of step',
    'fn.beat': 'Converts a time in seconds into a beat with the tempo map',
    'fn.sec': 'Converts a beat into seconds with the tempo map',

    // Reference: units
    'unit.px': 'Pixels (the number is unchanged)',
    'unit.pct':
      'Percent: 50% is 50, and u * 100% stays a percentage. It is a fraction when it scales a value: *= 50% halves, v * 120% is 1.2 times v. Between two values, 7 % 2 is the remainder',
    'unit.deg': 'Degrees (converted for sin, cos and tan)',
    'unit.s': 'Seconds, converted into the unit of the field',
    'unit.ms': 'Milliseconds, converted into the unit of the field',
    'unit.b': 'Beats',
    'unit.v':
      'Speed: 3v moves as far as speed 3 would during the segment (120 pixels per second per unit). With =, a distance alone is measured from the start of the segment: = 3v is the same as = v0 + 3v',

    // Reference: assignments
    'assign.set': 'Sets every selected item to the value',
    'assign.add': 'Adds to the current value',
    'assign.sub': 'Subtracts from the current value',
    'assign.mul': 'Multiplies the current value',
    'assign.div': 'Divides the current value',
  },
};

export default catalog;
